// ========================================
// カード固有効果レジストリ
// 各属性ファイルから効果をインポートして統合
// ========================================

import type { EffectContext } from '../../types';

// 属性別カード効果をインポート
import { fireCardEffects } from './fire';
import { waterCardEffects } from './water';
import { lightCardEffects } from './light';
import { darkCardEffects } from './dark';
import { primitiveCardEffects } from './primitive';
import { futureCardEffects } from './future';
import { neutralCardEffects } from './neutral';

/**
 * カード効果コンテキストの拡張型
 * EffectContextを拡張して追加のオプショナルプロパティを許可
 */
export type CardEffectContext = EffectContext & {
  /** 召喚酔いフラグ（デフォルトtrue） */
  summoningSickness?: boolean;
  /** その他の動的プロパティ */
  [key: string]: unknown;
};

/**
 * カード効果関数の型定義
 * @param skillText - 技テキスト（効果説明）
 * @param context - 効果コンテキスト（追加プロパティ許可）
 * @returns 効果が成功したかどうか
 */
export type CardEffectFunction = (skillText: string, context: CardEffectContext) => boolean;

/**
 * カード効果レジストリの型定義
 * カードID -> 効果関数のマッピング
 * Note: 既存JSコードとの互換性のため、any許容のレジストリ型を使用
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type CardEffectRegistry = Record<string, (skillText: string, context: any) => boolean>;

/**
 * 全カード固有効果のレジストリ
 * 各属性のエフェクトを自動的にマージ
 */
export const CARD_SPECIFIC_EFFECTS: CardEffectRegistry = {
  ...fireCardEffects,
  ...waterCardEffects,
  ...lightCardEffects,
  ...darkCardEffects,
  ...primitiveCardEffects,
  ...futureCardEffects,
  ...neutralCardEffects,
};

/**
 * カードIDから固有効果を取得
 * @param cardId - カードID (例: 'C0000279')
 * @returns 効果関数またはnull
 */
export const getCardEffect = (cardId: string): CardEffectFunction | null => {
  return CARD_SPECIFIC_EFFECTS[cardId] || null;
};

/**
 * カードに固有効果があるかチェック
 * @param cardId - カードID
 * @returns boolean
 */
export const hasCardEffect = (cardId: string): boolean => {
  return !!CARD_SPECIFIC_EFFECTS[cardId];
};

/**
 * 登録されているカード固有効果の数を取得
 * @returns number
 */
export const getRegisteredEffectCount = (): number => {
  return Object.keys(CARD_SPECIFIC_EFFECTS).length;
};
